import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { weekdayLabels } from "../lib/focusChart";
import { readRecentCourseIds, topRecentCourses } from "../lib/recentCourses";
import { Link, useNavigate } from "react-router";
import { Badge, Button, Card, Icon, StatCard } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import type { CalendarItem, Course, DashboardResponse, ProgressStats } from "@nelurio/contracts";
import { api } from "../api/client";
import { CalendarMonth } from "../components/CalendarMonth";
import { AiCoachCard } from "../components/AiCoachCard";
import { CourseCard } from "../components/CourseCard";
import { TodayPlan } from "../components/TodayPlan";
import { useFocusTimer } from "../focus/FocusTimerProvider";
import type { WidgetSize } from "./registry";

/** The one weighted score from the progress engine (same shape ProgressPage reads). */
interface AnalyticsOverview {
  from: string;
  to: string;
  days: number;
  score: number;
}

// Shared dashboard payload: widgets consume this context instead of refetching,
// and DISABLED widgets never mount — so hidden widgets never fetch at all.
export interface DashboardContextValue {
  data: DashboardResponse;
  stats: ProgressStats | null;
  courses: Course[];
  scheduled: Parameters<typeof TodayPlan>[0]["scheduled"];
  anytime: Parameters<typeof TodayPlan>[0]["anytime"];
  dueReviews: number;
  openTasks: number;
  routineCountToday: number;
  focusLabel: string;
  weekly: Array<{ weekStart: string; seconds: number }>;
  weeklyDays: Array<{ weekStart: string; seconds: number }>;
  maxWeekly: number;
  calendar: CalendarItem[];
  selectedDay: string | null;
  dayData: DashboardResponse | null;
  dayLoading: boolean;
  selectDay: (key: string) => void;
  clearDay: () => void;
  todayInteractive: boolean;
}

const DashboardDataContext = createContext<DashboardContextValue | null>(null);

export function DashboardDataProvider({ value, children }: { value: DashboardContextValue; children: React.ReactNode }) {
  return <DashboardDataContext.Provider value={value}>{children}</DashboardDataContext.Provider>;
}

function useDashboard(): DashboardContextValue {
  const value = useContext(DashboardDataContext);
  if (!value) throw new Error("dashboard widget used outside DashboardDataProvider");
  return value;
}

export function DashboardWidget({ id, size, render }: { id: string; size: WidgetSize; render: () => React.ReactNode }) {
  return (
    <div className={`dash-widget dash-widget--${size}`} data-widget={id}>
      {render()}
    </div>
  );
}

/* ---- per-widget components (markup identical to the pre-widget dashboard) ---- */

export function StatsWidget() {
  const { t, formatNumber } = useI18n();
  const ctx = useDashboard();
  return (
    <div className="stats-grid">
      <Link to="/tasks" className="stat-link">
        <StatCard label={t("stat.reviewsToday")} value={formatNumber(ctx.dueReviews)} note={t("stat.reviewsTodayNote")} icon="route" tone="purple" />
      </Link>
      <Link to="/tasks" className="stat-link">
        <StatCard
          label={t("stat.tasksToday")}
          value={formatNumber([...(ctx.scheduled || []), ...(ctx.anytime || [])].filter((task) => task.kind !== "exam" && task.kind !== "class" && task.kind !== "new_lesson" && task.status !== "completed").length)}
          note={t("stat.tasksTodayNote")}
          icon="check"
          tone="amber"
        />
      </Link>
      <Link to="/tasks" className="stat-link">
        <StatCard label={t("routine.title")} value={formatNumber(ctx.routineCountToday)} note={t("stat.routinesTodayNote")} icon="clock" tone="blue" />
      </Link>
      <Link to="/focus" className="stat-link">
        <StatCard label={t("stat.focusToday")} value={ctx.focusLabel} note={t("stat.focusTodayNote")} icon="tree" tone="green" />
      </Link>
    </div>
  );
}

export function TodayWidget() {
  const { t } = useI18n();
  const ctx = useDashboard();
  return (
    <TodayPlan
      scheduled={ctx.selectedDay ? (ctx.dayData?.scheduled ?? []) : ctx.scheduled}
      anytime={ctx.selectedDay ? (ctx.dayData?.anytime ?? []) : ctx.anytime}
      reviews={ctx.selectedDay ? (ctx.dayData?.reviews ?? []) : ctx.data.reviews}
      exams={ctx.selectedDay ? (ctx.dayData?.exams ?? []) : ctx.data.exams}
      date={ctx.selectedDay ?? undefined}
      headerAction={ctx.selectedDay ? { label: t("dashboard.backToToday"), onClick: ctx.clearDay } : undefined}
      loadFailed={Boolean(ctx.selectedDay) && !ctx.dayData && !ctx.dayLoading}
      onRetry={() => ctx.selectedDay && ctx.selectDay(ctx.selectedDay)}
    />
  );
}

export function CalendarWidget() {
  const ctx = useDashboard();
  // Selecting a day only makes sense while the today widget can show it.
  return <CalendarMonth items={ctx.calendar} compact selected={ctx.selectedDay ?? undefined} onSelect={ctx.todayInteractive ? ctx.selectDay : undefined} />;
}

export function CoursesWidget() {
  const { t } = useI18n();
  const ctx = useDashboard();
  return (
    <section className="dashboard-section">
      <div className="section-heading">
        <div>
          <span className="section-kicker">{t("common.today")}</span>
          <h2>{t("dashboard.activeCourses")}</h2>
        </div>
        <Link to="/courses">
          {t("common.viewAll")} <Icon name="arrow" size={15} />
        </Link>
      </div>
      {ctx.courses.length ? (
        <div className="course-grid">
          {topRecentCourses(ctx.courses, readRecentCourseIds()).map((course) => (
            <CourseCard course={course} key={course.id} />
          ))}
        </div>
      ) : (
        <Card className="dashboard-empty-course">
          <EmptyStateInline title={t("courses.emptyTitle")} body={t("courses.emptyBody")} cta={{ to: "/courses", label: t("courses.firstCourse") }} />
        </Card>
      )}
    </section>
  );
}

export function FocusWidget() {
  return <DashboardFocusCard />;
}

export function CoachWidget() {
  return <AiCoachCard kind="brief" />;
}

export function NextExamWidget() {
  const { t, formatDate, formatNumber } = useI18n();
  const ctx = useDashboard();
  const nextExam = ctx.data.summary?.nextExam ?? null;
  if (!nextExam) return null;
  return (
    <Card className="next-exam-card">
      <span className="next-exam-card__icon">
        <Icon name="warning" />
      </span>
      <div>
        <small>
          {t("dashboard.upcoming")} · {t("dashboard.exam")}
        </small>
        <strong>{nextExam.title}</strong>
        <span>
          {formatDate(nextExam.date)} · {t("dashboard.daysLeft", { count: formatNumber(nextExam.daysLeft) })}
        </span>
      </div>
    </Card>
  );
}

export function WeeklyFocusWidget() {
  const { t, locale, formatNumber } = useI18n();
  const ctx = useDashboard();
  const weekly = ctx.weekly;
  const todayKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;
  const maxDay = Math.max(1, ...ctx.weeklyDays.map((day) => day.seconds));
  return (
    <Card className="weekly-focus-card">
      <div className="section-heading compact">
        <div>
          <span className="section-kicker">{t("nav.progress")}</span>
          <h3>{t("dashboard.thisWeek")}</h3>
        </div>
      </div>
      {ctx.weeklyDays.length ? (
        <>
          <div className="weekly-bars" aria-label={t("dashboard.thisWeek")}>
            {ctx.weeklyDays.map((day) => (
              <i
                key={day.weekStart}
                style={{ height: `${day.seconds > 0 ? Math.max(8, Math.round((day.seconds / maxDay)) * 100) : 4}%` }}
                className={day.weekStart === todayKey ? "is-today" : ""}
                title={`${weekdayLabels(locale, [day.weekStart])[0]} · ${formatNumber(Math.round(day.seconds / 60))} ${t("common.minutes")}`}
              />
            ))}
          </div>
          <div className="weekly-bars__labels" aria-hidden="true">
            {weekdayLabels(locale, ctx.weeklyDays.map((day) => day.weekStart)).map((label, index) => (
              <small key={index}>{label}</small>
            ))}
          </div>
          <p className="weekly-total">{t("dashboard.weekTotal", { minutes: formatNumber(Math.round(ctx.weeklyDays.reduce((sum, day) => sum + day.seconds, 0) / 60)) })}</p>
        </>
      ) : (
        <p className="weekly-empty">{t("progress.noFocusYet")}</p>
      )}
    </Card>
  );
}

export function RecordWidget() {
  const { t } = useI18n();
  return (
    <Link to="/reports/new" className="dashboard-cta">
      <Button icon="sparkle" className="dashboard-cta__btn">{t("report.short")}</Button>
    </Link>
  );
}

const FLASHCARD_LIMIT = 5;

interface FlipCard { id: string; front: string; back: string; courseTitle: string; courseId: string; }

/** Quick flip-review over the active courses' decks (read-only practice —
 *  scheduling lives in the course tab). Fetches ≤3 decks, picks 5 random. */
export function FlashcardsWidget() {
  const { t, formatNumber } = useI18n();
  const ctx = useDashboard();
  const [cards, setCards] = useState<FlipCard[] | null>(null);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    let active = true;
    const decks = ctx.courses.slice(0, 3);
    Promise.all(
      decks.map((course) => api<Array<{ id: string; front: string; back: string }>>(`/courses/${course.id}/flashcards`).then((items) => ({ course, items: Array.isArray(items) ? items : [] })).catch(() => ({ course, items: [] })))
    ).then((results) => {
      if (!active) return;
      const pool: FlipCard[] = [];
      for (const { course, items } of results) {
        for (const item of items) pool.push({ id: item.id, front: item.front, back: item.back, courseTitle: course.title, courseId: course.id });
      }
      for (let i = pool.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [pool[i], pool[j]] = [pool[j], pool[i]];
      }
      setCards(pool.slice(0, FLASHCARD_LIMIT));
    });
    return () => {
      active = false;
    };
  }, [ctx.courses]);

  const card = cards && cards.length > 0 ? cards[Math.min(index, cards.length - 1)] : null;

  return (
    <Card className="widget-card flashcards-widget" elevated>
      <div className="widget-card__head">
        <span className="section-kicker">{t("nav.courses")}</span>
        <h2>{t("widget.flashcards")}</h2>
      </div>
      {!cards ? (
        <p className="widget-card__hint">{t("common.loading")}</p>
      ) : cards.length === 0 || !card ? (
        <p className="widget-card__hint">{t("widget.flashcardsEmpty")}</p>
      ) : (
        <>
          <button
            className={`flashcards-widget__card ${revealed ? "is-revealed" : ""}`}
            onClick={() => setRevealed((current) => !current)}
            aria-label={revealed ? t("widget.flashcardsHide") : t("widget.flashcardsReveal")}
          >
            <small>{card.courseTitle}</small>
            <strong>{revealed ? card.back : card.front}</strong>
            <span className="flashcards-widget__step">{formatNumber(index + 1)} / {formatNumber(cards.length)}</span>
          </button>
          <div className="flashcards-widget__actions">
            {index > 0 && (
              <Button onClick={() => { setIndex(index - 1); setRevealed(false); }}>{t("common.back")}</Button>
            )}
            {index < cards.length - 1 ? (
              <Button icon="arrow" onClick={() => { setIndex(index + 1); setRevealed(false); }}>{t("widget.flashcardsNext")}</Button>
            ) : (
              <Link to={`/courses/${card.courseId}?tab=flashcards`}>
                <Button icon="book">{t("widget.flashcardsOpen")}</Button>
              </Link>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

const PLANNER_DAYS = 7;

/** Next-7-days strip from the calendar payload (counts per day, today first). */
function formatWeekday(dateKey: string, locale: string): string {
  const date = new Date(`${dateKey}T12:00:00`);
  return new Intl.DateTimeFormat(locale, { weekday: "short" }).format(date);
}

export function PlannerWidget() {
  const { t, locale, formatDate, formatNumber } = useI18n();
  const ctx = useDashboard();
  const days = useMemo(() => {
    const buckets: Array<{ key: string; count: number; isToday: boolean }> = [];
    const now = new Date();
    for (let offset = 0; offset < PLANNER_DAYS; offset++) {
      const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
      buckets.push({ key, count: 0, isToday: offset === 0 });
    }
    for (const item of ctx.calendar) {
      const bucket = buckets.find((day) => day.key === item.date);
      if (bucket) bucket.count += 1;
    }
    return buckets;
  }, [ctx.calendar]);

  return (
    <Card className="widget-card planner-widget" elevated>
      <div className="widget-card__head">
        <span className="section-kicker">{t("nav.planner")}</span>
        <h2>{t("widget.planner")}</h2>
      </div>
      <div className="planner-widget__strip">
        {days.map((day) => (
          <button
            key={day.key}
            className={`planner-widget__day ${day.isToday ? "is-today" : ""} ${day.count > 0 ? "has-items" : ""}`}
            onClick={() => ctx.todayInteractive && ctx.selectDay(day.key)}
            aria-label={formatDate(day.key)}
          >
            <small>{formatWeekday(day.key, locale)}</small>
            <strong>{formatNumber(day.count)}</strong>
          </button>
        ))}
      </div>
    </Card>
  );
}

export function ProgressWidget() {
  const { t, formatNumber } = useI18n();
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);

  useEffect(() => {
    let active = true;
    api<AnalyticsOverview>("/analytics/overview?days=7")
      .then((response) => {
        if (active) setOverview(response);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const score = Math.round(Number(overview?.score ?? 0));

  return (
    <Card className="widget-card progress-widget" elevated>
      <div className="widget-card__head">
        <span className="section-kicker">{t("nav.progress")}</span>
        <h2>{t("widget.progress")}</h2>
      </div>
      {!overview ? (
        <p className="widget-card__hint">{t("common.loading")}</p>
      ) : (
        <div className="progress-widget__body">
          <span className="progress-widget__ring" style={{ background: `conic-gradient(var(--nl-purple) ${score * 3.6}deg, var(--nl-border, #e4e4f0) 0deg)` }} aria-hidden="true">
            <i>{formatNumber(score)}</i>
          </span>
          <Link to="/progress" className="progress-widget__link">
            {t("common.viewAll")} <Icon name="arrow" size={14} />
          </Link>
        </div>
      )}
    </Card>
  );
}

interface PlannedSemesterLite { id: string; name: string; credits: number; status: "planned" | "active" | "completed"; modules?: Array<{ credits: number }>; }

/** Current-term mini progress from the device-local academic path store. */
export function AcademicWidget() {
  const { t, formatNumber } = useI18n();
  const [term, setTerm] = useState<{ name: string; done: number; planned: number } | null>(null);

  useEffect(() => {
    try {
      const semesters = JSON.parse(window.localStorage.getItem("nelurio.semesters.v1") ?? "[]") as PlannedSemesterLite[];
      const active = Array.isArray(semesters) ? semesters.find((semester) => semester.status === "active") || semesters.find((semester) => semester.status === "planned") : undefined;
      if (!active) {
        setTerm(null);
        return;
      }
      const moduleCredits = (active.modules || []).reduce((sum, module) => sum + (Number(module.credits) || 0), 0);
      setTerm({ name: active.name, done: active.status === "completed" ? moduleCredits || active.credits : 0, planned: moduleCredits || active.credits });
    } catch {
      setTerm(null);
    }
  }, []);

  return (
    <Card className="widget-card academic-widget" elevated>
      <div className="widget-card__head">
        <span className="section-kicker">{t("academic.title")}</span>
        <h2>{term ? term.name : t("widget.academic")}</h2>
      </div>
      {!term ? (
        <div className="academic-widget__empty">
          <Badge tone="purple">{t("widget.academicEmpty")}</Badge>
        </div>
      ) : (
        <>
          <div className="academic-widget__bar" role="progressbar" aria-valuemin={0} aria-valuemax={term.planned} aria-valuenow={term.done}>
            <i style={{ width: `${term.planned > 0 ? Math.min(100, Math.round((term.done / term.planned) * 100)) : 0}%` }} />
          </div>
          <small>{formatNumber(term.done)} / {formatNumber(term.planned)} {t("widget.ects")}</small>
          <Link to="/academic-path" className="widget-card__cta">
            {t("common.viewAll")} <Icon name="arrow" size={13} />
          </Link>
        </>
      )}
    </Card>
  );
}

/** Library storage usage + material count from the stats payload. */
export function LibraryWidget() {
  const { t, formatNumber } = useI18n();
  const ctx = useDashboard();
  const resources = ctx.stats?.resources;
  const usedMb = resources ? resources.byteTotal / (1024 * 1024) : 0;
  const quotaMb = 2048;
  return (
    <Card className="widget-card library-widget" elevated>
      <div className="widget-card__head">
        <span className="section-kicker">{t("nav.library")}</span>
        <h2>{t("widget.library")}</h2>
      </div>
      {resources ? (
        <>
          <div className="library-widget__row">
            <Badge tone="blue">{formatNumber(resources.total)}</Badge>
            <span>{t("widget.materials")}</span>
          </div>
          <div className="academic-widget__bar" role="progressbar" aria-valuemin={0} aria-valuemax={quotaMb} aria-valuenow={Math.round(usedMb)}>
            <i style={{ width: `${Math.min(100, Math.round((usedMb / quotaMb) * 100))}%` }} />
          </div>
          <small>{`${formatNumber(Math.round(usedMb * 10) / 10)} MB / ${formatNumber(2)} GB`}</small>
          <Link to="/library" className="widget-card__cta">
            {t("common.viewAll")} <Icon name="arrow" size={13} />
          </Link>
        </>
      ) : (
        <p className="widget-card__hint">{t("common.loading")}</p>
      )}
    </Card>
  );
}

/** Focus card moved verbatim from DashboardPage (self-managed timer state). */
const RECENT_KEY = "nelurio.focus.recent";

function readRecentDurations(): number[] {
  try {
    const raw = window.localStorage.getItem(RECENT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed.slice(0, 4);
    }
  } catch { /* ignore */ }
  return [15, 25, 45, 60];
}

export function rememberDuration(minutes: number) {
  try {
    const current = readRecentDurations().filter((value) => value !== minutes);
    window.localStorage.setItem(RECENT_KEY, JSON.stringify([minutes, ...current].slice(0, 4)));
  } catch { /* ignore */ }
}

function DashboardFocusCard() {
  const { t, formatNumber } = useI18n();
  const focus = useFocusTimer();
  const navigate = useNavigate();
  const mm = String(Math.floor(focus.secondsLeft / 60)).padStart(2, "0");
  const ss = String(focus.secondsLeft % 60).padStart(2, "0");

  return (
    <Card className={`dash-focus-card ${focus.running ? "is-running" : ""}`}>
      <div className="dash-focus-card__ring"><Icon name="timer" /></div>
      <div className="dash-focus-card__body">
        <span className="section-kicker">{t("focus.title")}</span>
        {focus.running ? (
          <>
            <h2>{mm}:{ss}{focus.paused ? ` · ${t("focus.paused")}` : ""}</h2>
            <p>{focus.goal || t("focus.running")}</p>
          </>
        ) : (
          <>
            <h2>{t("dashFocus.title")}</h2>
            <p>{t("dashFocus.subtitle")}</p>
          </>
        )}
      </div>
      <div className="dash-focus-card__actions">
        {focus.running ? (
          <>
            <Button variant="secondary" icon="route" onClick={() => navigate("/focus")}>{t("dashFocus.open")}</Button>
            <Button icon="check" onClick={() => void focus.stopAndSave()}>{t("focus.finish")}</Button>
          </>
        ) : (
          <div className="dash-focus-rings" role="group" aria-label={t("focus.duration")}>
            {readRecentDurations().map((minutes) => (
              <button key={minutes} className="dash-focus-ring" onClick={() => { focus.start(minutes, "", ""); navigate("/focus"); }}>
                <span style={{ "--ring": `${Math.min(100, (minutes / 120) * 100)}%` } as React.CSSProperties} />
                <strong>{formatNumber(minutes)}</strong>
                <small>{t("common.minutes")}</small>
              </button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export function EmptyStateInline({ title, body, cta }: { title: string; body: string; cta: { to: string; label: string } }) {
  return (
    <div className="empty-inline">
      <span className="empty-inline__icon">✦</span>
      <div>
        <strong>{title}</strong>
        <p>{body}</p>
      </div>
      <Link to={cta.to}><Button variant="secondary" icon="plus">{cta.label}</Button></Link>
    </div>
  );
}
